import { DocumentRecordStatus } from '@/__generated__/graphql';
import { InfoIcon } from '@/assets/icons/info-icons/info';
import { PlusIcon } from '@/assets/icons/info-icons/plus';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { GENERATE_ANSWERS_MUTATION, PROCESS_AI_ASSET_MUTATION } from '@/components/aiAssistant/queries';
import { Banner } from '@/components/basicComponents/banner';
import FileUpload from '@/components/basicComponents/fileUpload';
import Input, { ErrorText } from '@/components/basicComponents/input';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import RadioButton from '@/components/basicComponents/radioButton';
import { DropFileArea } from '@/components/dueDiligence-page/dueDiligenceRecord/dropFileArea';
import FileNotSupported from '@/components/dueDiligence-page/modals/fileNotSupported';
import { allowFileExtensions } from '@/components/settings-page/importData/utils';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import axios from 'axios';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { NavigateOptions, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { STRATEGY_DETAILS_PAGE_CREATE_DOCUMENT_RECORD_MUTATION } from '../queries';

interface IForm {
  skipUpload: boolean;
  name: string;
  files: File[];
}

const defaultFormValues: IForm = {
  skipUpload: false,
  name: '',
  files: []
};

const getCurrentFormattedDate = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');

  return `${year}.${month}.${day}`;
};

const fileExtensions = ['PDF'];

interface AddUpdateProps {
  isOpen: boolean;
  manager: string | null;
  openEditManagerUpdatePage: (updateId: string, options?: NavigateOptions) => void;
  onClose: () => void;
}

const AddUpdate = ({ isOpen, manager, openEditManagerUpdatePage, onClose }: AddUpdateProps) => {
  const params = useParams();
  const { default: defaultErrorMessage } = useErrorMessages();
  const { control, getValues, setValue, handleSubmit, watch } = useForm<IForm>({ defaultValues: defaultFormValues });

  const [showNotSupportedDialog, setShowNotSupportedDialog] = useState(false);
  const [drag, setDrag] = useState(false);
  const [showDropFileArea, setShowDropFileArea] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [createDocumentRecord] = useMutation(STRATEGY_DETAILS_PAGE_CREATE_DOCUMENT_RECORD_MUTATION);
  const [processAIAsset] = useMutation(PROCESS_AI_ASSET_MUTATION);
  const [generateAnswers] = useMutation(GENERATE_ANSWERS_MUTATION);

  const onDropHandler = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const uploadFiles = e.dataTransfer.files;
    setDrag(false);
    if (!allowFileExtensions(uploadFiles, fileExtensions).length) {
      setShowNotSupportedDialog(true);
      return;
    }
    const files = getValues('files');
    setValue('files', [...files, uploadFiles[0]], { shouldValidate: true });
    setShowDropFileArea(false);
  };

  const handleUploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const uploadFiles = e.target.files;
    if (!allowFileExtensions(uploadFiles, fileExtensions).length) {
      setShowNotSupportedDialog(true);
      return;
    }
    const files = getValues('files');
    setValue('files', [...files, uploadFiles[0]], { shouldValidate: true });
    setShowDropFileArea(false);
  };

  const deleteFilePreview = (file: File) => {
    const files = getValues('files');
    const updatedFiles = files.filter((f) => `${f.name}-${f.size}-${f.lastModified}` !== `${file.name}-${file.size}-${file.lastModified}`);
    setValue('files', updatedFiles);
  };

  const uploadFile = async (file: File, recordId: string) => {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await axios.post(
      `${process.env.REACT_APP_BACKEND_BASE_URL}/investment-offering-materials-attachment/investment/${params?.strategyId}/document-record/${recordId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    );

    await processAIAsset({
      variables: {
        data: {
          investmentId: params?.strategyId,
          recordId: recordId,
          filename: data.fileName,
          type: 'mu',
          assetKey: data.assetKey
        }
      }
    });

    return data;
  };

  const onSave = async (data: IForm) => {
    try {
      setErrorMessage(null);
      setLoading(true);
      const { name, files, skipUpload } = data;

      const { data: createDocumentRecordData } = await createDocumentRecord({
        variables: {
          data: {
            name: skipUpload ? name : `New Update ${getCurrentFormattedDate()}`,
            status: DocumentRecordStatus.Draft,
            documentType: 'mu',
            manager,
            investmentId: params?.strategyId
          }
        }
      });

      const recordId = createDocumentRecordData.createDocumentRecord.id;

      const filenames = [];
      if (!skipUpload) {
        for await (const file of files) {
          const { fileName } = await uploadFile(file, recordId);
          filenames.push(fileName);
        }

        generateAnswers({
          variables: {
            data: {
              investmentId: params?.strategyId,
              recordId: recordId,
              type: 'mu',
              filenames
            }
          }
        });

        openEditManagerUpdatePage(createDocumentRecordData.createDocumentRecord.id, { state: { openAiPane: true } });
        onClose();
        return;
      }

      openEditManagerUpdatePage(createDocumentRecordData.createDocumentRecord.id);
      onClose();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (showNotSupportedDialog) {
    return <FileNotSupported isOpen={showNotSupportedDialog} onClose={() => setShowNotSupportedDialog(false)} />;
  }

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title="Add New Update"
      confirmButton={{
        name: 'Save',
        onClick: handleSubmit(onSave),
        disabled: loading
      }}
      showCloseIcon={!loading}
      showCancelButton={!loading}
      isUseOutsideClick={!loading || showNotSupportedDialog}
    >
      {loading ? (
        <ModalLoader />
      ) : (
        <>
          {errorMessage && (
            <ErrorBannerWrapper>
              <Banner
                icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                title="Error"
                description={errorMessage}
                bgColor={themeVar('context.error')}
              />
            </ErrorBannerWrapper>
          )}
          <ModalBody>
            <RadioButtonsWrapper>
              <Controller
                name="skipUpload"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <>
                    {['Upload Documents', 'Add Update Manually'].map((option, index) => (
                      <RadioButton
                        key={option + index}
                        name="radioButtonValue"
                        radioButtonText={option}
                        selectedOption={value ? 'Add Update Manually' : 'Upload Documents'}
                        handleRadioChange={(event) => onChange(event.target.value === 'Add Update Manually')}
                      />
                    ))}
                  </>
                )}
              />
            </RadioButtonsWrapper>
            <Banner
              title=""
              description={
                watch('skipUpload')
                  ? 'You will start with a blank form. You can enter commentary manually or upload documents later.'
                  : 'Upload a quarterly investor letter, fact sheet, or any other document type containing information or commentary for this update.'
              }
              icon={<InfoIcon width={26} height={26} fill={themeVar('font.base')} />}
              bgColor={themeVar('layer.1')}
              color={themeVar('font.base')}
            />
            {watch('skipUpload') ? (
              <Controller
                name="name"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: 'Name is required'
                  }
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    label="Name"
                    value={value || ''}
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                    errorText={error?.message}
                    isRequiredField
                    size="md"
                  />
                )}
              />
            ) : (
              <>
                <Controller
                  name="files"
                  control={control}
                  rules={{
                    validate: {
                      hasFiles: (files: File[]) => files.length > 0 || 'At least one file is required'
                    }
                  }}
                  render={({ field: { value: files }, fieldState: { error } }) => (
                    <div>
                      {!!files.length && (
                        <FilesPreviewWrapper>
                          {files.map((file) => (
                            <FileUpload
                              key={`${file.name}-${file.size}-${file.lastModified}`}
                              type={file.type.includes('image') ? 'image' : 'document'}
                              imageSrc={URL.createObjectURL(file)}
                              fileName={file.name}
                              deleteFile={() => deleteFilePreview(file)}
                            />
                          ))}
                        </FilesPreviewWrapper>
                      )}
                      {(showDropFileArea || !files.length) && (
                        <DropFileArea
                          id="signed-signature-packet"
                          handleUploadFile={handleUploadFile}
                          onDropHandler={onDropHandler}
                          drag={drag}
                          setDrag={setDrag}
                        />
                      )}
                      {error?.message && <ErrorText size="md">{error?.message}</ErrorText>}

                      {!showDropFileArea && !!files.length && files.length < 4 && (
                        <AddMoreFilesButton onClick={() => setShowDropFileArea(true)}>
                          <PlusIcon width={20} height={20} fill={themeVar('font.action')} />
                          <span>Add More Files</span>
                        </AddMoreFilesButton>
                      )}
                    </div>
                  )}
                />
              </>
            )}
          </ModalBody>
        </>
      )}
    </ModalWrappedContent>
  );
};

export default AddUpdate;

const ModalBody = styled.div<{ alignItems?: string }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ alignItems }) => alignItems || 'stretch'};
  gap: 10px;
`;

const RadioButtonsWrapper = styled.div`
  display: flex;
`;

const FilesPreviewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AddMoreFilesButton = styled.div`
  margin: 10px auto 0;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  width: max-content;
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  padding: 4px 30px;
  color: ${themeVar('font.action')};
`;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 10px;
`;
